import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useWallet } from '../WalletProvider'
import { useNotification } from '../NotificationSystem'

const FormContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
`

const FormSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 2rem;

  h3 {
    font-size: 1.25rem;
    color: var(--color-text);
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
      width: 20px;
      height: 20px;
      color: var(--color-primary);
    }
  }

  h4 {
    font-size: 1rem;
    color: var(--color-text);
    margin: 2rem 0 1rem 0;
  }
`

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--color-text);
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: var(--color-text-secondary);
  }
`

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--color-text);
  font-size: 0.875rem;
  transition: all 0.2s ease;
  margin-bottom: 1rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  option {
    background: #1a1a1a;
    color: var(--color-text);
  }
`

const TypeSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`

const TypeOption = styled.button`
  background: ${props => props.$selected ? 'rgba(59, 130, 246, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$selected ? 'var(--color-primary)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  padding: 1rem;
  color: var(--color-text);
  text-align: left;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &:hover {
    background: rgba(59, 130, 246, 0.1);
    transform: translateY(-2px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${props => {
      switch (props.$type) {
        case 'x_management': return 'var(--color-primary)'
        case 'github': return 'var(--color-secondary)'
        case 'website': return 'var(--color-accent)'
        default: return 'var(--color-text-secondary)'
      }
    }};
    opacity: ${props => props.$selected ? '1' : '0'};
    transition: opacity 0.2s ease;
  }

  h4 {
    font-size: 1rem;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  p {
    font-size: 0.875rem;
    color: var(--color-text-secondary);
  }
`

const AgentAllocation = styled.div`
  display: grid;
  gap: 1.5rem;
`

const AgentSlider = styled.div`
  label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    color: var(--color-text);
    font-size: 0.875rem;

    span {
      color: var(--color-text-secondary);
    }
  }

  input[type="range"] {
    width: 100%;
    height: 4px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
    appearance: none;
    cursor: pointer;

    &::-webkit-slider-thumb {
      appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: ${props => {
        switch (props.$type) {
          case 'coder': return 'var(--color-primary)'
          case 'artist': return 'var(--color-secondary)'
          case 'social': return 'var(--color-accent)'
          default: return 'var(--color-text)'
        }
      }};
      cursor: pointer;
      transition: transform 0.2s ease;

      &:hover {
        transform: scale(1.2);
      }
    }
  }
`

const PreviewPanel = styled.div`
  position: sticky;
  top: 2rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
`

const PreviewTitle = styled.h3`
  font-size: 1.25rem;
  color: var(--color-text);
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`

const PreviewContent = styled.div`
  h4 {
    font-size: 0.875rem;
    color: var(--color-text-secondary);
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--color-text);
    margin-bottom: 1.5rem;
  }
`

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
  margin-right: 8px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`

const DeployButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: 0.5s;
  }

  &:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

    &::before {
      left: 100%;
    }
  }

  &:disabled {
    background: linear-gradient(135deg, 
      rgba(59, 130, 246, 0.5), 
      rgba(16, 185, 129, 0.5)
    );
    cursor: not-allowed;
    transform: none;
  }
`

const SaveDraftButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: transparent;
  color: var(--color-text);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1rem;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`

const AgentPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.5rem;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.02);

  .icon {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => {
      switch (props.$type) {
        case 'coder': return 'rgba(59, 130, 246, 0.1)'
        case 'artist': return 'rgba(16, 185, 129, 0.1)'
        case 'social': return 'rgba(249, 115, 22, 0.1)'
        default: return 'rgba(255, 255, 255, 0.1)'
      }
    }};
    color: ${props => {
      switch (props.$type) {
        case 'coder': return 'var(--color-primary)'
        case 'artist': return 'var(--color-secondary)'
        case 'social': return 'var(--color-accent)'
        default: return 'var(--color-text)'
      }
    }};
  }

  .details {
    flex: 1;

    h5 {
      font-size: 0.875rem;
      color: var(--color-text);
    }

    p {
      font-size: 0.75rem;
      color: var(--color-text-secondary);
      margin: 0;
    }
  }
`

const UploadArea = styled.div`
  border: 2px dashed rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-primary);
    background: rgba(59, 130, 246, 0.05);
  }

  svg {
    width: 32px;
    height: 32px;
    color: var(--color-text-secondary);
    margin-bottom: 1rem;
  }

  p {
    color: var(--color-text-secondary);
    margin-bottom: 0.5rem;
  }

  span {
    font-size: 0.875rem;
    color: var(--color-primary);
  }
`

const CheckboxGroup = styled.div`
  display: grid;
  gap: 1rem;
  margin-bottom: 1.5rem;

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--color-text);
    font-size: 0.875rem;
    cursor: pointer;

    input[type="checkbox"] {
      width: 16px;
      height: 16px;
      border-radius: 4px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      background: rgba(255, 255, 255, 0.05);
      cursor: pointer;

      &:checked {
        background: var(--color-primary);
        border-color: var(--color-primary);
      }
    }
  }
`

const projectTypes = [
  {
    id: 'x_management',
    name: 'X Account Management',
    description: 'Automated social media management with content creation and engagement.',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
      </svg>
    )
  },
  {
    id: 'github',
    name: 'GitHub Repository',
    description: 'Create and initialize repositories with custom configurations.',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.604-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.464-1.11-1.464-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.167 22 16.418 22 12c0-5.523-4.477-10-10-10z"/>
      </svg>
    )
  },
  {
    id: 'website',
    name: 'Website Development',
    description: 'Build custom websites with modern features and designs.',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
      </svg>
    )
  }
]

function ProjectRequestForm() {
  const { wallet } = useWallet()
  const { addNotification } = useNotification()
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    agents: {
      coders: 0,
      artists: 0,
      social: 0
    },
    resources: [],
    deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    config: {
      x_management: {
        postFrequency: 'daily',
        contentType: ['text', 'image'],
        engagementGoal: 'moderate'
      },
      github: {
        repoName: '',
        language: 'javascript',
        includeTests: true,
        includeDocs: true
      },
      website: {
        template: 'custom',
        features: ['contact', 'blog'],
        responsive: true,
        darkMode: true
      }
    }
  })

  const handleTypeSelect = (type) => {
    setFormData(prev => ({
      ...prev,
      type,
      agents: {
        coders: type === 'github' ? 3 : type === 'website' ? 5 : 1,
        artists: type === 'website' ? 2 : 1,
        social: type === 'x_management' ? 3 : 1
      }
    }))
  }

  const handleAgentChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      agents: {
        ...prev.agents,
        [type]: parseInt(value)
      }
    }))
  }

  const handleDrop = (e) => {
    e.preventDefault()
    const files = Array.from(e.dataTransfer.files)
    setFormData(prev => ({
      ...prev,
      resources: [...prev.resources, ...files]
    }))
  }

  const validateForm = () => {
    if (!formData.name.trim()) {
      addNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Please enter a project name.',
        duration: 3000
      })
      return false
    }

    if (!formData.type) {
      addNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Please select a project type.',
        duration: 3000
      })
      return false
    }

    if (formData.type === 'github' && !formData.config.github.repoName.trim()) {
      addNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Please enter a repository name.',
        duration: 3000
      })
      return false
    }

    const totalAgents = Object.values(formData.agents).reduce((sum, count) => sum + count, 0)
    if (totalAgents === 0) {
      addNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Please allocate at least one agent.',
        duration: 3000
      })
      return false
    }

    return true
  }

  const [isDeploying, setIsDeploying] = useState(false)

  const handleDeploy = async () => {
    if (!validateForm()) return

    setIsDeploying(true)
    addNotification({
      type: 'info',
      title: 'Deploying Project',
      message: 'Initializing agents and setting up project...',
      duration: 2000
    })

    try {
      // Simulate deployment process
      await new Promise(resolve => setTimeout(resolve, 2000))

      const totalAgents = Object.values(formData.agents).reduce((sum, count) => sum + count, 0)
      addNotification({
        type: 'success',
        title: 'Project Deployed',
        message: `Project "${formData.name}" has been successfully deployed. ${totalAgents} agents have been initialized and are ready to work.`,
        duration: 5000
      })

      // Reset form or redirect to project dashboard
      // setFormData(initialFormState) // Uncomment to reset form
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Deployment Failed',
        message: 'There was an error deploying your project. Please try again.',
        duration: 5000
      })
    } finally {
      setIsDeploying(false)
    }
  }

  const handleSaveDraft = () => {
    if (!formData.name.trim()) {
      addNotification({
        type: 'warning',
        title: 'Missing Project Name',
        message: 'Please enter a project name before saving as draft.',
        duration: 3000
      })
      return
    }

    addNotification({
      type: 'info',
      title: 'Draft Saved',
      message: `Project "${formData.name}" configuration has been saved as a draft. You can resume configuration at any time.`,
      duration: 3000
    })
  }

  return (
    <FormContainer>
      <div>
        {/* Project Details Section */}
        <FormSection>
          <h3>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
            Project Details
          </h3>
          <Input
            type="text"
            placeholder="Enter project name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          />
        </FormSection>

        {/* Project Type Section */}
        <FormSection>
          <h3>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h16M4 12h16m-7 6h7"/>
            </svg>
            Project Type
          </h3>
          <TypeSelector>
            {projectTypes.map(type => (
              <TypeOption
                key={type.id}
                $type={type.id}
                $selected={formData.type === type.id}
                onClick={() => handleTypeSelect(type.id)}
              >
                <h4>
                  {type.icon}
                  {type.name}
                </h4>
                <p>{type.description}</p>
              </TypeOption>
            ))}
          </TypeSelector>

          {/* Type-specific Configuration */}
          {formData.type === 'x_management' && (
            <div>
              <h4>X Account Configuration</h4>
              <Select
                value={formData.config.x_management.postFrequency}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  config: {
                    ...prev.config,
                    x_management: {
                      ...prev.config.x_management,
                      postFrequency: e.target.value
                    }
                  }
                }))}
              >
                <option value="daily">Daily Posts</option>
                <option value="weekly">Weekly Posts</option>
                <option value="custom">Custom Schedule</option>
              </Select>

              <CheckboxGroup>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.config.x_management.contentType.includes('text')}
                    onChange={(e) => {
                      const newTypes = e.target.checked
                        ? [...formData.config.x_management.contentType, 'text']
                        : formData.config.x_management.contentType.filter(t => t !== 'text')
                      setFormData(prev => ({
                        ...prev,
                        config: {
                          ...prev.config,
                          x_management: {
                            ...prev.config.x_management,
                            contentType: newTypes
                          }
                        }
                      }))
                    }}
                  />
                  Text Posts
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.config.x_management.contentType.includes('image')}
                    onChange={(e) => {
                      const newTypes = e.target.checked
                        ? [...formData.config.x_management.contentType, 'image']
                        : formData.config.x_management.contentType.filter(t => t !== 'image')
                      setFormData(prev => ({
                        ...prev,
                        config: {
                          ...prev.config,
                          x_management: {
                            ...prev.config.x_management,
                            contentType: newTypes
                          }
                        }
                      }))
                    }}
                  />
                  Image Posts
                </label>
              </CheckboxGroup>

              <Select
                value={formData.config.x_management.engagementGoal}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  config: {
                    ...prev.config,
                    x_management: {
                      ...prev.config.x_management,
                      engagementGoal: e.target.value
                    }
                  }
                }))}
              >
                <option value="moderate">Moderate Engagement</option>
                <option value="high">High Engagement</option>
                <option value="maximum">Maximum Growth</option>
              </Select>
            </div>
          )}

          {formData.type === 'github' && (
            <div>
              <h4>Repository Configuration</h4>
              <Input
                type="text"
                placeholder="Repository Name"
                value={formData.config.github.repoName}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  config: {
                    ...prev.config,
                    github: {
                      ...prev.config.github,
                      repoName: e.target.value
                    }
                  }
                }))}
              />

              <Select
                value={formData.config.github.language}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  config: {
                    ...prev.config,
                    github: {
                      ...prev.config.github,
                      language: e.target.value
                    }
                  }
                }))}
              >
                <option value="javascript">JavaScript</option>
                <option value="typescript">TypeScript</option>
                <option value="python">Python</option>
                <option value="rust">Rust</option>
              </Select>

              <CheckboxGroup>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.config.github.includeTests}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      config: {
                        ...prev.config,
                        github: {
                          ...prev.config.github,
                          includeTests: e.target.checked
                        }
                      }
                    }))}
                  />
                  Include Test Suite
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.config.github.includeDocs}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      config: {
                        ...prev.config,
                        github: {
                          ...prev.config.github,
                          includeDocs: e.target.checked
                        }
                      }
                    }))}
                  />
                  Include Documentation
                </label>
              </CheckboxGroup>
            </div>
          )}

          {formData.type === 'website' && (
            <div>
              <h4>Website Configuration</h4>
              <Select
                value={formData.config.website.template}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  config: {
                    ...prev.config,
                    website: {
                      ...prev.config.website,
                      template: e.target.value
                    }
                  }
                }))}
              >
                <option value="custom">Custom Build</option>
                <option value="portfolio">Portfolio Template</option>
                <option value="business">Business Template</option>
                <option value="ecommerce">E-Commerce Template</option>
              </Select>

              <CheckboxGroup>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.config.website.features.includes('contact')}
                    onChange={(e) => {
                      const newFeatures = e.target.checked
                        ? [...formData.config.website.features, 'contact']
                        : formData.config.website.features.filter(f => f !== 'contact')
                      setFormData(prev => ({
                        ...prev,
                        config: {
                          ...prev.config,
                          website: {
                            ...prev.config.website,
                            features: newFeatures
                          }
                        }
                      }))
                    }}
                  />
                  Contact Form
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.config.website.features.includes('blog')}
                    onChange={(e) => {
                      const newFeatures = e.target.checked
                        ? [...formData.config.website.features, 'blog']
                        : formData.config.website.features.filter(f => f !== 'blog')
                      setFormData(prev => ({
                        ...prev,
                        config: {
                          ...prev.config,
                          website: {
                            ...prev.config.website,
                            features: newFeatures
                          }
                        }
                      }))
                    }}
                  />
                  Blog System
                </label>
              </CheckboxGroup>

              <CheckboxGroup>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.config.website.responsive}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      config: {
                        ...prev.config,
                        website: {
                          ...prev.config.website,
                          responsive: e.target.checked
                        }
                      }
                    }))}
                  />
                  Responsive Design
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.config.website.darkMode}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      config: {
                        ...prev.config,
                        website: {
                          ...prev.config.website,
                          darkMode: e.target.checked
                        }
                      }
                    }))}
                  />
                  Dark Mode Support
                </label>
              </CheckboxGroup>
            </div>
          )}
        </FormSection>

        {/* Agent Allocation Section */}
        <FormSection>
          <h3>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
            Agent Allocation
          </h3>
          <AgentAllocation>
            <AgentSlider $type="coder">
              <label>
                Coders
                <span>{formData.agents.coders} allocated</span>
              </label>
              <input
                type="range"
                min="0"
                max="10"
                value={formData.agents.coders}
                onChange={(e) => handleAgentChange('coders', e.target.value)}
              />
            </AgentSlider>
            <AgentSlider $type="artist">
              <label>
                Artists
                <span>{formData.agents.artists} allocated</span>
              </label>
              <input
                type="range"
                min="0"
                max="5"
                value={formData.agents.artists}
                onChange={(e) => handleAgentChange('artists', e.target.value)}
              />
            </AgentSlider>
            <AgentSlider $type="social">
              <label>
                Social Media
                <span>{formData.agents.social} allocated</span>
              </label>
              <input
                type="range"
                min="0"
                max="5"
                value={formData.agents.social}
                onChange={(e) => handleAgentChange('social', e.target.value)}
              />
            </AgentSlider>
          </AgentAllocation>
        </FormSection>

        {/* Resources Section */}
        <FormSection>
          <h3>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3 3m0 0l-3-3m3 3v-7"/>
            </svg>
            Resources
          </h3>
          <UploadArea
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3 3m0 0l-3-3m3 3v-7"/>
            </svg>
            <p>Drag and drop your files here</p>
            <span>or click to browse</span>
          </UploadArea>
        </FormSection>

        {/* Deadline Section */}
        <FormSection>
          <h3>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            Project Deadline
          </h3>
          <Input
            type="date"
            value={formData.deadline}
            min={new Date().toISOString().split('T')[0]}
            onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
          />
        </FormSection>
      </div>

      {/* Preview Panel */}
      <PreviewPanel>
        <PreviewTitle>Project Summary</PreviewTitle>
        <PreviewContent>
          <h4>Project Name</h4>
          <p>{formData.name || 'Untitled Project'}</p>

          <h4>Project Type</h4>
          <p>{projectTypes.find(t => t.id === formData.type)?.name || 'Not selected'}</p>

          <h4>Assigned Agents</h4>
          {formData.agents.coders > 0 && (
            <AgentPreview $type="coder">
              <div className="icon">C</div>
              <div className="details">
                <h5>Coders</h5>
                <p>{formData.agents.coders} agents assigned</p>
              </div>
            </AgentPreview>
          )}
          {formData.agents.artists > 0 && (
            <AgentPreview $type="artist">
              <div className="icon">A</div>
              <div className="details">
                <h5>Artists</h5>
                <p>{formData.agents.artists} agents assigned</p>
              </div>
            </AgentPreview>
          )}
          {formData.agents.social > 0 && (
            <AgentPreview $type="social">
              <div className="icon">S</div>
              <div className="details">
                <h5>Social Media</h5>
                <p>{formData.agents.social} agents assigned</p>
              </div>
            </AgentPreview>
          )}

          <h4>Resources</h4>
          <p>{formData.resources.length} files attached</p>

          {formData.type === 'x_management' && (
            <>
              <h4>X Account Settings</h4>
              <p>
                Post Frequency: {formData.config.x_management.postFrequency}
                <br />
                Content Types: {formData.config.x_management.contentType.join(', ')}
                <br />
                Engagement Goal: {formData.config.x_management.engagementGoal}
              </p>
            </>
          )}

          {formData.type === 'github' && (
            <>
              <h4>Repository Settings</h4>
              <p>
                Repository Name: {formData.config.github.repoName || 'Not set'}
                <br />
                Language: {formData.config.github.language}
                <br />
                Features: {[
                  formData.config.github.includeTests && 'Test Suite',
                  formData.config.github.includeDocs && 'Documentation'
                ].filter(Boolean).join(', ')}
              </p>
            </>
          )}

          {formData.type === 'website' && (
            <>
              <h4>Website Settings</h4>
              <p>
                Template: {formData.config.website.template}
                <br />
                Features: {formData.config.website.features.join(', ')}
                <br />
                {formData.config.website.responsive && 'Responsive Design'}
                {formData.config.website.responsive && formData.config.website.darkMode && ', '}
                {formData.config.website.darkMode && 'Dark Mode Support'}
              </p>
            </>
          )}

          <h4>Deadline</h4>
          <p>{new Date(formData.deadline).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}</p>

          <DeployButton
            onClick={handleDeploy}
            disabled={!formData.name || !formData.type || isDeploying}
          >
            <ButtonContent>
              {isDeploying && <Spinner />}
              {isDeploying ? 'Deploying...' : 'Deploy Project'}
            </ButtonContent>
          </DeployButton>
          <SaveDraftButton onClick={handleSaveDraft}>
            Save as Draft
          </SaveDraftButton>
        </PreviewContent>
      </PreviewPanel>
    </FormContainer>
  )
}

export default ProjectRequestForm
